import { CheckCircle, DoctorWithStethoscope, Left } from "@repo/icons";
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import { HeaderUpButton, HeaderRoot, HeaderTitle } from "@/components/header";
import { Page } from "@/components/page";
import {
  PageTitleDescription,
  PageTitleMainContent,
  PageTitleRoot,
} from "@/components/page-title/page-title-root";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import {
  EmptyStateImage,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/empty-state";
import {
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/message-drawer";
import { PatientList } from "@/components/patient-list";

export const FollowUpAppointmentReminderPatientsPageQuery = graphql(/* GraphQL */ `
  query FollowUpAppointmentReminderPatientsPageQuery {
    ...PatientListQueryFragment
  }
`);

export const FollowUpAppointmentReminderPatientsPage = (): JSX.Element => {
  const router = useRouter();

  const queryResult = useGraphQL(FollowUpAppointmentReminderPatientsPageQuery);
  const navigate = useNavigate();

  const { data } = queryResult;

  const { action } = useSearch({
    from: "/settings/assist/follow-up-appointment-reminder/patients/",
  });

  const handleDrawerDismiss = (value: boolean): void => {
    if (!value) {
      router.history.back();
    }
  };

  const onPatientClick = (patient: { patientId: number }): void => {
    void navigate({
      to: "/waiting-room/follow-up-appointment-reminder/patient/$patientId",
      params: { patientId: String(patient.patientId) },
    });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderUpButton icon={Left} align="start" />
        <HeaderTitle title="Pacientes" />
      </HeaderRoot>

      <Page>
        <PageTitleRoot>
          <AsyncDataWrapper {...queryResult}>
            {data ? (
              <>
                <PageTitleMainContent>
                  Selecione o paciente que deseja criar lembrete
                </PageTitleMainContent>
                <PageTitleDescription>
                  Seu paciente será notificado no momento ideal para marcar uma consulta.
                </PageTitleDescription>

                <div className="mt-4">
                  <PatientList patientList={data} onClick={onPatientClick} />
                </div>
              </>
            ) : (
              <EmptyStateRoot>
                <EmptyStateImage
                  icon={DoctorWithStethoscope}
                  alt="Lista de pacientes vazia"
                />
                <EmptyStateTitle>
                  Você ainda não possui pacientes cadastrados
                </EmptyStateTitle>
              </EmptyStateRoot>
            )}
          </AsyncDataWrapper>
        </PageTitleRoot>
      </Page>

      <MessageDrawerRoot
        open={action === "open-patient-with-reminder-drawer"}
        setOpen={handleDrawerDismiss}
        icon={CheckCircle}
        variant="secondary"
      >
        <MessageDrawerTitle>Seu lembrete já está programado</MessageDrawerTitle>
        <MessageDrawerBody>
          <p className="mb-6">
            É possível enviar apenas um lembrete por vez, caso queira enviar um novo
            lembrete cancele a mensagem já configurada e crie um novo
          </p>
        </MessageDrawerBody>
      </MessageDrawerRoot>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/assist/follow-up-appointment-reminder/patients/",
)({
  component: FollowUpAppointmentReminderPatientsPage,
});
